import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAccounting } from "@/contexts/AccountingContext";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import {
  FileText, BookOpen, BarChart3, Users, Package, Receipt,
  Search, Plus, Settings, Download, Upload, Shield, Bell,
  Zap, Calculator, TrendingUp, Smartphone, Globe, Landmark,
  CreditCard, Wallet, GitBranch, Bot, Mic, Star,
} from "lucide-react";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CommandAction {
  id: string;
  label: string;
  description?: string;
  icon: typeof FileText;
  category: "create" | "navigate" | "search" | "action";
  action: () => void;
  keywords?: string[];
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { vouchers, ledgers, stockItems } = useAccounting();
  const [search, setSearch] = useState("");

  // Reset search when opened
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const go = useCallback((path: string) => {
    navigate(path);
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const actions: CommandAction[] = useMemo(() => [
    // Quick Create
    { id: "create-contra", label: "Contra Voucher", description: "F4 — Cash / Bank transfer", icon: Plus, category: "create", action: () => go("/dashboard/journals?type=contra&action=new"), keywords: ["contra", "deposit", "withdraw", "transfer"] },
    { id: "create-payment", label: "Payment Voucher", description: "F5 — Record payment", icon: Plus, category: "create", action: () => go("/dashboard/journals?type=payment&action=new"), keywords: ["pay", "send", "disbursement"] },
    { id: "create-receipt", label: "Receipt Voucher", description: "F6 — Record receipt", icon: Plus, category: "create", action: () => go("/dashboard/journals?type=receipt&action=new"), keywords: ["receive", "collect", "receipt"] },
    { id: "create-journal", label: "Create Journal Entry", description: "F7 — Manual journal voucher", icon: Plus, category: "create", action: () => go("/dashboard/journals?type=journal&action=new"), keywords: ["new", "entry", "voucher", "journal"] },
    { id: "create-sales", label: "Create Sales Invoice", description: "F8 — New GST sales invoice", icon: Plus, category: "create", action: () => go("/dashboard/sales"), keywords: ["new", "invoice", "bill", "sale"] },
    { id: "create-purchase", label: "Create Purchase Invoice", description: "F9 — New purchase entry", icon: Plus, category: "create", action: () => go("/dashboard/purchase"), keywords: ["new", "buy", "vendor", "purchase"] },
    { id: "create-expense", label: "Record Expense", description: "Add new expense", icon: Plus, category: "create", action: () => go("/dashboard/expenses"), keywords: ["new", "spend", "cost"] },

    // Navigation
    { id: "nav-dashboard", label: "Dashboard", description: "Home overview", icon: BarChart3, category: "navigate", action: () => go("/dashboard"), keywords: ["home", "overview"] },
    { id: "nav-ledgers", label: "Ledgers", description: "Chart of accounts", icon: BookOpen, category: "navigate", action: () => go("/dashboard/ledgers"), keywords: ["accounts", "chart"] },
    { id: "nav-invoices", label: "Invoices", description: "All invoices & notes", icon: FileText, category: "navigate", action: () => go("/dashboard/invoices"), keywords: ["bills", "sales"] },
    { id: "nav-inventory", label: "Inventory", description: "Stock & items", icon: Package, category: "navigate", action: () => go("/dashboard/inventory"), keywords: ["stock", "items", "products"] },
    { id: "nav-parties", label: "Customers & Suppliers", description: "Customers & suppliers directory", icon: Users, category: "navigate", action: () => go("/dashboard/parties"), keywords: ["customers", "suppliers", "parties", "clients"] },
    { id: "nav-reports", label: "Reports", description: "Financial reports", icon: BarChart3, category: "navigate", action: () => go("/dashboard/reports"), keywords: ["profit", "loss", "balance"] },
    { id: "nav-gst", label: "GST Dashboard", description: "GST filings & returns", icon: Receipt, category: "navigate", action: () => go("/dashboard/gst"), keywords: ["tax", "returns", "filing"] },
    { id: "nav-gst-reports", label: "GST Reports", description: "GSTR-1, GSTR-3B", icon: Receipt, category: "navigate", action: () => go("/dashboard/gst-reports"), keywords: ["gstr", "tax report"] },
    { id: "nav-reconciliation", label: "Reconciliation", description: "Bank reconciliation", icon: Calculator, category: "navigate", action: () => go("/dashboard/reconciliation"), keywords: ["bank", "match"] },
    { id: "nav-expenses", label: "Expenses", description: "Expense tracker", icon: CreditCard, category: "navigate", action: () => go("/dashboard/expenses"), keywords: ["spend", "costs"] },
    { id: "nav-payroll", label: "Payroll", description: "Salary & wages", icon: Wallet, category: "navigate", action: () => go("/dashboard/payroll"), keywords: ["salary", "employees"] },
    { id: "nav-cash-flow", label: "Cash Flow", description: "Cash flow analysis", icon: TrendingUp, category: "navigate", action: () => go("/dashboard/cash-flow"), keywords: ["money", "flow"] },
    { id: "nav-branches", label: "Branches", description: "Multi-branch management", icon: GitBranch, category: "navigate", action: () => go("/dashboard/branches"), keywords: ["office", "location"] },
    { id: "nav-bank-sync", label: "Bank Sync", description: "Bank feed integration", icon: Landmark, category: "navigate", action: () => go("/dashboard/bank-sync"), keywords: ["bank", "feed"] },
    { id: "nav-ca-marketplace", label: "CA Marketplace", description: "Find a chartered accountant", icon: Globe, category: "navigate", action: () => go("/dashboard/ca-marketplace"), keywords: ["accountant", "ca"] },
    { id: "nav-documents", label: "Documents", description: "File manager", icon: FileText, category: "navigate", action: () => go("/dashboard/documents"), keywords: ["files", "upload"] },
    { id: "nav-settings", label: "Settings", description: "App settings", icon: Settings, category: "navigate", action: () => go("/dashboard/settings"), keywords: ["preferences", "config"] },

    // Actions
    { id: "act-export", label: "Export Data", description: "Ctrl+E — Download data", icon: Download, category: "action", action: () => go("/dashboard/export"), keywords: ["download", "csv"] },
    { id: "act-import", label: "Import Data", description: "Upload CSV/Excel", icon: Upload, category: "action", action: () => go("/dashboard/import"), keywords: ["upload", "csv", "excel"] },
    { id: "act-ai-assistant", label: "AI Assistant", description: "Ask AI anything", icon: Bot, category: "action", action: () => go("/dashboard/ai-assistant"), keywords: ["ai", "help", "chat"] },
    { id: "act-ai-search", label: "AI Search", description: "Smart search with AI", icon: Search, category: "action", action: () => go("/dashboard/ai-search"), keywords: ["find", "smart"] },
    { id: "act-voice", label: "Voice Accounting", description: "Speak to create entries", icon: Mic, category: "action", action: () => go("/dashboard/voice-accounting"), keywords: ["speak", "voice"] },
    { id: "act-automation", label: "Automation Rules", description: "Auto-categorization", icon: Zap, category: "action", action: () => go("/dashboard/automation"), keywords: ["rules", "auto"] },
    { id: "act-e-invoice", label: "E-Invoice / IRN", description: "Generate e-invoices", icon: FileText, category: "action", action: () => go("/dashboard/e-invoice"), keywords: ["irn", "einvoice"] },
    { id: "act-security", label: "Security", description: "Audit & access control", icon: Shield, category: "action", action: () => go("/dashboard/security"), keywords: ["audit", "access"] },
    { id: "act-notifications", label: "Notifications", description: "Alerts & reminders", icon: Bell, category: "action", action: () => go("/dashboard/notifications"), keywords: ["alerts"] },
  ], [go]);

  // Dynamic search results from data
  const dynamicResults = useMemo(() => {
    if (search.length < 2) return [];
    const q = search.toLowerCase();
    const results: CommandAction[] = [];

    // Search customers/parties from ledgers
    ledgers
      .filter(l => l.group === "Sundry Debtors" || l.group === "Sundry Creditors")
      .filter(l => l.name.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach(l => {
        results.push({
          id: `ledger-${l.id}`,
          label: l.name,
          description: `${l.group} · ₹${Math.abs(l.currentBalance).toLocaleString("en-IN")}`,
          icon: Users,
          category: "search",
          action: () => go("/dashboard/ledgers"),
          keywords: [],
        });
      });

    // Search inventory items
    stockItems
      .filter(i => i.name.toLowerCase().includes(q) || i.hsnCode?.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach(i => {
        results.push({
          id: `item-${i.id}`,
          label: i.name,
          description: `HSN: ${i.hsnCode || "—"} · ₹${i.currentRate.toLocaleString("en-IN")}`,
          icon: Package,
          category: "search",
          action: () => go("/dashboard/inventory"),
          keywords: [],
        });
      });

    // Search invoices
    vouchers
      .filter(v => 
        v.invoiceNumber?.toLowerCase().includes(q) || 
        v.voucherNumber.toLowerCase().includes(q) ||
        v.partyName?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .forEach(v => {
        results.push({
          id: `voucher-${v.id}`,
          label: v.invoiceNumber || v.voucherNumber,
          description: `${v.type} · ${v.partyName || "—"} · ₹${v.totalDebit.toLocaleString("en-IN")}`,
          icon: FileText,
          category: "search",
          action: () => go("/dashboard/invoices"),
          keywords: [],
        });
      });

    return results;
  }, [search, ledgers, stockItems, vouchers, go]);

  const categoryLabels: Record<string, { label: string; icon: typeof FileText }> = {
    create: { label: "Quick Create", icon: Plus },
    navigate: { label: "Go To", icon: Star },
    search: { label: "Search Results", icon: Search },
    action: { label: "Actions", icon: Zap },
  };

  const groupOrder = ["create", "search", "navigate", "action"] as const;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Type a command or search..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-4">
            <Search className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No results found</p>
            <p className="text-xs text-muted-foreground/60">Try a different search term</p>
          </div>
        </CommandEmpty>

        {groupOrder.map((cat, idx) => {
          const items = cat === "search"
            ? dynamicResults
            : actions.filter(a => a.category === cat);
          if (items.length === 0) return null;
          const { label, icon: Icon } = categoryLabels[cat];
          return (
            <div key={cat}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={
                <span className="flex items-center gap-1.5">
                  <Icon className="h-3 w-3" />
                  {label}
                </span>
              }>
                {items.map(item => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.description || ""} ${item.keywords?.join(" ") || ""}`}
                    onSelect={() => item.action()}
                    className="flex items-center gap-3 py-2.5"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted border border-border">
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          );
        })}
      </CommandList>
      <div className="border-t border-border px-3 py-2 flex items-center gap-3 text-[10px] text-muted-foreground">
        <span><kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono">↑↓</kbd> navigate</span>
        <span><kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono">Enter</kbd> select</span>
        <span><kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono">Esc</kbd> close</span>
      </div>
    </CommandDialog>
  );
}
